import { useEffect, useState, type ReactNode } from 'react';
import { money } from '@busbook/ui';

/**
 * The screen that tells a passenger whether their money moved.
 *
 * ONE CENTRED COLUMN, NO CARD. A frame adds nothing to a screen holding a single
 * idea, and nothing should compete with the answer: mark, answer, one line of
 * explanation, the amount, then somewhere to go.
 *
 * THE MARKS ARE DRAWN, NEVER EMOJI. ✓ ✕ ⊘ render as a different shape, weight and
 * colour on every platform, and this is not the screen to accept that on.
 *
 * THE AMOUNT IS ALWAYS SHOWN. It is the first thing anyone checks, and it was
 * missing from every version of this screen before.
 *
 * Colours come from the TENANT's palette, not a fixed one: this app is
 * white-labeled and the operator's brand is what a passenger recognises. Only the
 * shapes and the hierarchy are shared with the other Baraka products.
 */

type Tone = 'confirmed' | 'failed' | 'cancelled' | 'waiting';

const STROKE = { fill: 'none', strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

/**
 * Whether the viewer asked for less motion.
 *
 * Read in JS, not CSS, because this arc spins with SVG's own <animateTransform>
 * and SMIL is NOT governed by a `prefers-reduced-motion` media query or by the
 * global `animation-duration: 0.01ms` override — it keeps turning regardless.
 * Verified rather than assumed: the element was still present and animating under
 * an explicitly reduced-motion context.
 */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

function Mark({ tone, colour }: { tone: Tone; colour: string }) {
  const reduced = usePrefersReducedMotion();

  if (tone === 'waiting') {
    return (
      <svg viewBox="0 0 32 32" className="h-12 w-12" role="img" aria-hidden>
        {/* A payment being confirmed is a process with an end, and an arc has a
            direction where a pulse does not. */}
        <circle cx="16" cy="16" r="12" stroke={colour} opacity={0.25} {...STROKE} />
        {/* No meaning lives in the motion: without it this is a static ring with
            a marked segment, which still reads as "in progress". */}
        <path d="M16 4a12 12 0 0 1 12 12" stroke={colour} {...STROKE}>
          {!reduced && (
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 16 16"
              to="360 16 16"
              dur="1.1s"
              repeatCount="indefinite"
            />
          )}
        </path>
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 32 32" className="h-12 w-12" role="img" aria-hidden>
      <circle cx="16" cy="16" r="14" stroke={colour} opacity={0.28} {...STROKE} />
      {tone === 'confirmed' && <path d="M9.5 16.5l4.5 4.5L23 12" stroke={colour} {...STROKE} />}
      {tone === 'failed' && <path d="M11 11l10 10M21 11L11 21" stroke={colour} {...STROKE} />}
      {tone === 'cancelled' && <path d="M11 21L21 11" stroke={colour} {...STROKE} />}
    </svg>
  );
}

export function PaymentStatus({
  tone,
  title,
  detail,
  amountCents,
  currency,
  reference,
  children,
}: {
  tone: Tone;
  title: string;
  detail: string;
  amountCents?: number;
  currency?: string;
  reference?: string;
  /** Somewhere to go. Omitted while a payment is still settling — see below. */
  children?: ReactNode;
}) {
  const colour =
    tone === 'confirmed'
      ? 'var(--color-secondary)'
      : tone === 'failed'
        ? 'var(--signal-cancelled)'
        : tone === 'cancelled'
          ? 'var(--signal-delayed)'
          : 'var(--color-primary)';

  return (
    <section className="grid min-h-[60vh] place-items-center px-6 py-10">
      <div className="w-full max-w-sm text-center">
        <div className="flex justify-center">
          <Mark tone={tone} colour={colour} />
        </div>

        <h1 className="mt-5 text-xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-2 text-sm" style={{ color: 'var(--color-muted)' }}>
          {detail}
        </p>

        {amountCents !== undefined && currency && (
          <p className="mt-5 text-2xl font-semibold tabular" style={{ color: 'var(--color-text)' }}>
            {money(amountCents, currency)}
          </p>
        )}

        {reference && (
          <p className="mt-1 text-xs tabular" style={{ color: 'var(--color-faint)' }}>
            {reference}
          </p>
        )}

        {/*
          * Somewhere to go, but ONLY once the outcome has settled.
          *
          * Offering "back to trips" while a payment is still confirming invites
          * someone to leave at the one moment they do not want to — so a waiting
          * screen passes no children, and says instead that closing costs nothing.
          */}
        {children && <div className="mt-7 flex flex-col gap-2">{children}</div>}
      </div>
    </section>
  );
}
